import { Drawer, Box, Typography, IconButton, Divider } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { ROUTES, AppRoutes } from '@consts/routes';
import { checkPathHasPermission } from '@utils/permissions';
import { routeActive } from '@utils/routes';
import { colors, typography as typographyTokens, radii } from '@components/design-system/tokens';

import type { RootState } from '@features/redux/store';
import type { RouteSegment, RouteItem } from '@/types/routes';
import type { RoleUtils } from '@/types/permissions';

interface MobileDrawerProps {
    sidebarOpen: boolean;
    handleToggle: () => void;
}

// Section title mapping
const SECTION_TITLES: Record<string, string> = {
    reports: 'Reports & Analytics',
    activity: 'Cut Activity',
    manage: 'Management',
};

const NavItem = ({ route, isActive, onClick }: { route: RouteItem; isActive: boolean; onClick: () => void }) => (
    <Box
        component={RouterLink}
        to={route.link}
        onClick={onClick}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderRadius: `${radii.md}px`,
            textDecoration: 'none',
            color: isActive ? colors.primary[600] : colors.neutral[700],
            backgroundColor: isActive ? colors.primary[50] : 'transparent',
            fontWeight: isActive ? typographyTokens.fontWeight.semibold : typographyTokens.fontWeight.medium,
            fontSize: typographyTokens.fontSize.sm,
            transition: 'all 150ms ease',
            '&:active': {
                backgroundColor: colors.neutral[100],
                transform: 'scale(0.98)',
            },
        }}>
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                '& svg': {
                    fontSize: 20,
                    color: isActive ? colors.primary[500] : colors.neutral[500],
                },
            }}>
            {isActive ? route.IconActive : route.Icon}
        </Box>
        <Typography
            sx={{
                fontSize: typographyTokens.fontSize.sm,
                fontWeight: 'inherit',
                color: 'inherit',
                textTransform: 'capitalize',
            }}>
            {route.label}
        </Typography>
    </Box>
);

const NavSection = ({ segment, role, onClick }: { segment: RouteSegment; role: RoleUtils; onClick: () => void }) => {
    const availableRoutes = segment.routes.filter((route) => checkPathHasPermission(role, route.link));

    if (availableRoutes.length === 0) return null;

    return (
        <Box sx={{ mb: 2 }}>
            <Typography
                sx={{
                    fontSize: typographyTokens.fontSize.xs,
                    fontWeight: typographyTokens.fontWeight.semibold,
                    color: colors.neutral[400],
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    px: 2,
                    mb: 1,
                }}>
                {SECTION_TITLES[segment.name] || segment.name}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {availableRoutes.map((route) => (
                    <NavItem key={route.link} route={route} isActive={Boolean(routeActive(route))} onClick={onClick} />
                ))}
            </Box>
        </Box>
    );
};

export const MobileDrawer = ({ sidebarOpen, handleToggle }: MobileDrawerProps) => {
    const role = useSelector((state: RootState) => state.auth.Roles.role);

    const availableSegments = useMemo(
        () => ROUTES.filter((segment) => segment.routes.some((route) => checkPathHasPermission(role, route.link))),
        [role]
    );

    return (
        <Drawer
            variant='temporary'
            anchor='left'
            open={sidebarOpen}
            onClose={handleToggle}
            PaperProps={{
                sx: {
                    width: '85%',
                    maxWidth: 320,
                    backgroundColor: colors.neutral[0],
                    borderRight: 'none',
                    boxShadow: '4px 0 24px rgba(0, 0, 0, 0.12)',
                },
            }}
            ModalProps={{
                keepMounted: true,
            }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    borderBottom: `1px solid ${colors.neutral[200]}`,
                }}>
                <RouterLink to={AppRoutes.Insights} onClick={handleToggle}>
                    <img src='/logo.png' alt='Logo' style={{ height: 32 }} />
                </RouterLink>
                <IconButton
                    onClick={handleToggle}
                    size='small'
                    sx={{
                        color: colors.neutral[500],
                        '&:hover': {
                            backgroundColor: colors.neutral[100],
                        },
                    }}>
                    <CloseIcon fontSize='small' />
                </IconButton>
            </Box>

            {/* Navigation */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    py: 2,
                    px: 1,
                }}>
                {availableSegments.map((segment) => (
                    <NavSection key={segment.name} segment={segment} role={role} onClick={handleToggle} />
                ))}
            </Box>
        </Drawer>
    );
};
